import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useLazyLoadQuery, graphql } from 'react-relay/hooks'
import { useNotifications, useAnalytics, useAuth } from 'hooks'
import { Modal } from 'elements'
import CreateClientStep from './CreateClientStep'
import FirstClientUploadReport from '../../report/create/FirstClientUploadReport'
import FirstClientIntegrationSubmitStep from '../../report/create/FirstClientIntegrationSubmitStep'
import datePeriods from 'shared/datasets/date-periods'
import useReportCreate from '../../report/hooks/useReportCreate'
import useRunReportWithIntegration from '../../report/hooks/useRunReportWithIntegration'
import FirstClientXeroConnectionChoices from '../../integrations/FirstClientXeroConnectionChoices'

const DEFAULT_REPORT_PERIOD = datePeriods().find((p) => p.value === 'last-year')

// modal size to avoid scroll
const MODAL_SIZE = '755px'

export default function FirstClientModal(props) {
  const { modal, integrationResult } = props

  const [step, setStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [reportPeriod, setReportPeriod] = useState(DEFAULT_REPORT_PERIOD)
  const [reportName, setReportName] = useState('')
  const [validReportPeriod, setValidReportPeriod] = useState(false)
  const [noCurrentTenant, setNoCurrentTenant] = useState(false)
  const [companyId, setCompanyId] = useState(props.companyId || '')
  const notifications = useNotifications()
  const [createReport] = useReportCreate()
  const [runReportWithIntegration] = useRunReportWithIntegration()
  const analytics = useAnalytics()
  const history = useHistory()
  const { currentCompany } = useAuth()

  useEffect(() => {
    setValidReportPeriod(reportPeriod?.interval)
  }, [reportPeriod])

  let defaultChoice = { kind: 'intuit' }

  if (integrationResult?.integrationKind) {
    defaultChoice = { kind: integrationResult.integrationKind }
  } else if (integrationResult?.integrationId) {
    defaultChoice = { rowId: integrationResult.integrationId }
  }

  const [choice, setChoice] = useState(defaultChoice)

  const data = useLazyLoadQuery(
    graphql`
      query FirstClientModalQuery($companyId: ID!) {
        companyById(id: $companyId) {
          id
          ...FirstClientIntegrationChoices_company
          ...CreateClientStep_company
          integrations(first: 100) {
            nodes {
              ...FirstClientXeroConnectionChoices_integration
            }
          }
        }
      }
    `,
    { companyId },
  )

  const integration = data?.companyById?.integrations?.nodes?.[0]

  // set no current tenant for xero integrations
  useEffect(() => {
    // choice.id is defined when the integration has already been connected
    if (!choice?.id) {
      return
    }

    if (choice.kind === 'xero' && !choice.config.xero.currentTenant) {
      setNoCurrentTenant(true)
    }
  }, [choice, setNoCurrentTenant])

  // set company id after creation
  useEffect(() => {
    if (currentCompany) {
      setCompanyId(currentCompany.id)
    }
  }, [currentCompany])

  const handleNext = () => setStep(step + 1)
  const handleBack = () => setStep(step - 1)

  const handleCreateIntegrationReport = async () => {
    setSubmitting(true)

    const { report } = await createReport({
      companyId: data.companyById.id,
      kind: 'anomaly:1',
      name: reportName,
      periodStart: reportPeriod.interval.start.toISODate(),
      periodEnd: reportPeriod.interval.end.toISODate(),
    })

    // call the runReportWithIntegration lambda, but don't wait for it since
    // we'll get updates via report subscription
    runReportWithIntegration({
      reportId: report.id,
      integrationId: choice.id,
    })

    analytics.track('Report Created', { reportId: report.rowId })
    analytics.track('First Client Flow Completed', {
      companyId: data.companyById.id,
      reportId: report.rowId,
    })

    notifications.info('Report created.')
    modal.onClose()
    history.push(`companies/${companyId}/reports`)
  }

  let content = null

  if (step === 0) {
    content = (
      <CreateClientStep
        integrationResult={integrationResult}
        company={data?.companyById}
        selected={choice}
        onSelect={setChoice}
        onNext={handleNext}
        onClose={modal.onClose}
      />
    )
  } else if (step === 1 && noCurrentTenant) {
    content = (
      <FirstClientXeroConnectionChoices
        integration={integration}
        onDone={() => {
          setNoCurrentTenant(false)
        }}
        onBack={handleBack}
        onClose={modal.onClose}
      />
    )
  } else if (choice === 'upload') {
    content = (
      <FirstClientUploadReport
        step={step}
        submitting={submitting}
        setSubmitting={setSubmitting}
        onNext={handleNext}
        onBack={handleBack}
        onClose={modal.onClose}
      />
    )
  } else {
    content = (
      <FirstClientIntegrationSubmitStep
        onBack={handleBack}
        onClose={modal.onClose}
        submitting={submitting}
        reportPeriod={reportPeriod}
        setReportPeriod={setReportPeriod}
        reportName={reportName}
        setReportName={setReportName}
        valid={validReportPeriod}
        handleCreate={handleCreateIntegrationReport}
      />
    )
  }

  return (
    <Modal modal={modal} width={MODAL_SIZE} height={MODAL_SIZE}>
      {content}
    </Modal>
  )
}
